import React, { useState, useMemo } from 'react';
import { ChevronLeft, Lock, Globe, Users, Trash2, Eye, EyeOff, Loader2, ShieldAlert, Sparkles, RefreshCw, X, AlertTriangle, Check, Heart, Filter, ArrowUpDown, EyeIcon, Skull } from 'lucide-react';
import { supabase } from '../lib/supabase.ts';
import { VaultVisibility, UIImage, UserEntitlements } from '../types.ts';

interface VaultViewProps {
  user: any;
  entitlements: UserEntitlements;
  allHistory: UIImage[];
  setAllHistory: React.Dispatch<React.SetStateAction<UIImage[]>>;
  vaultVisibility: VaultVisibility;
  setVaultVisibility: (val: VaultVisibility) => void;
  fetchUserHistory: (id: string) => Promise<void>;
  setPreviewImage: (img: UIImage | null) => void;
  notify: (msg: string, type?: any) => void;
  setActiveView: (view: any) => void;
}

type SortBy = 'date' | 'rarity' | 'favorite';

const RARITY_ORDER = { 'god': 7, 'mythic': 6, 'legendary': 5, 'epic': 4, 'rare': 3, 'uncommon': 2, 'common': 1 };

export default function VaultView({ 
  user, entitlements, allHistory, setAllHistory, vaultVisibility, 
  setVaultVisibility, fetchUserHistory, setPreviewImage, 
  notify, setActiveView 
}: VaultViewProps) {
  
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState<boolean | null>(null);
  const [pendingDeleteMeme, setPendingDeleteMeme] = useState<UIImage | null>(null);
  
  // Sorting & Filtering State
  const [sortBy, setSortBy] = useState<SortBy>('date');
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  const filteredAndSortedHistory = useMemo(() => {
    let list = [...(allHistory || []).filter(Boolean)];

    if (favoritesOnly) {
      list = list.filter(m => m.is_favorite);
    }

    list.sort((a, b) => {
      if (sortBy === 'date') {
        return (b.timestamp || 0) - (a.timestamp || 0);
      }
      if (sortBy === 'rarity') {
        const orderA = RARITY_ORDER[a.rarity as keyof typeof RARITY_ORDER] || 0;
        const orderB = RARITY_ORDER[b.rarity as keyof typeof RARITY_ORDER] || 0;
        if (orderA !== orderB) return orderB - orderA;
        return (b.timestamp || 0) - (a.timestamp || 0);
      }
      if (sortBy === 'favorite') {
        if (a.is_favorite && !b.is_favorite) return -1;
        if (!a.is_favorite && b.is_favorite) return 1;
        return (b.timestamp || 0) - (a.timestamp || 0);
      }
      return 0;
    });

    return list;
  }, [allHistory, sortBy, favoritesOnly]);

  const vaultStoragePercent = entitlements?.vaultLimit === 'unlimited' ? 0 : (allHistory.length / (entitlements?.vaultLimit as number || 1)) * 100;

  const handleManualRefresh = async () => {
    if (!user?.id || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await fetchUserHistory(user.id);
      notify("VAULT SYNCED", "success");
    } catch (e) {
      notify("SYNC FAILED", "error");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleUpdateGlobalVisibility = async (val: VaultVisibility) => {
    if (!user?.id) return notify("LOGIN REQUIRED", "error");
    try {
      const { error } = await supabase.from('profiles').update({ vault_visibility: val }).eq('id', user.id);
      if (error) throw error;
      setVaultVisibility(val);
      notify(`MASTER LOCK: ${val.toUpperCase()}`, "success");
    } catch (e: any) { 
      notify(e.message?.includes('fetch') ? "CONNECTION BLOCKED" : "SIGNAL BLOCKED", "error"); 
    }
  };

  const handleToggleMemePublic = async (meme: UIImage) => {
    if (isProcessing || !user?.id) return;
    setIsProcessing(meme.id);
    const nextState = !meme.is_public;
    
    setAllHistory(prev => (prev || []).map(m => m && m.id === meme.id ? { ...m, is_public: nextState } : m));

    try {
      const { error } = await supabase.from('memes').update({ is_public: nextState }).eq('id', meme.id).eq('user_id', user.id);
      if (error) throw error;
      notify(nextState ? "SHOWCASED IN TOP ROT" : "REMOVED FROM SHOWCASE", "success");
    } catch (e: any) {
      setAllHistory(prev => (prev || []).map(m => m && m.id === meme.id ? { ...m, is_public: !nextState } : m));
      notify("ENCRYPTION FAILED", "error");
    } finally {
      setIsProcessing(null);
    }
  };

  const handleToggleFavorite = async (meme: UIImage) => {
    if (isProcessing || !user?.id) return;
    setIsProcessing(meme.id);
    const nextState = !meme.is_favorite;
    
    setAllHistory(prev => (prev || []).map(m => m && m.id === meme.id ? { ...m, is_favorite: nextState } : m));

    try {
      const { error } = await supabase.from('memes').update({ is_favorite: nextState }).eq('id', meme.id).eq('user_id', user.id);
      if (error) throw error;
      notify(nextState ? "ROT FAVORITED" : "REMOVED FROM FAVORITES", "info");
    } catch (e: any) {
      setAllHistory(prev => (prev || []).map(m => m && m.id === meme.id ? { ...m, is_favorite: !nextState } : m));
      notify("FAVORITE SYNC FAILED", "error");
    } finally {
      setIsProcessing(null);
    }
  };

  const executeBulkVisibility = async (showcase: boolean) => {
    setIsProcessing('bulk');
    setPendingBulkAction(null);

    const originalHistory = [...allHistory];
    setAllHistory(prev => (prev || []).map(m => m ? { ...m, is_public: showcase } : m));

    try {
      const { error } = await supabase
        .from('memes')
        .update({ is_public: showcase })
        .eq('user_id', user.id);

      if (error) throw error;
      
      notify(showcase ? "ALL ROTS SHOWCASED!" : "ALL ROTS ENCRYPTED!", "success");
    } catch (e: any) {
      setAllHistory(originalHistory);
      notify(e.message?.includes('fetch') ? "FETCH FAILED. Disable Ad-Blockers." : "BULK UPDATE FAILED", "error");
    } finally {
      setTimeout(() => setIsProcessing(null), 800);
    }
  };

  const handleDeleteClick = (meme: UIImage) => {
    setPendingDeleteMeme(meme);
  };

  const executeDeleteMeme = async () => {
    if (!pendingDeleteMeme || !user?.id) return;
    
    const memeId = pendingDeleteMeme.id;
    setIsProcessing(memeId);
    setPendingDeleteMeme(null);

    try {
      const { error } = await supabase.from('memes').delete().eq('id', memeId).eq('user_id', user.id);
      if (error) throw error;
      setAllHistory(prev => (prev || []).filter(m => m && m.id !== memeId));
      notify("ROT PURGED FROM REALITY", "info");
    } catch (e) {
      notify("PURGE FAILED", "error");
    } finally {
      setIsProcessing(null);
    }
  };

  const getRarityStyles = (rarity?: string) => {
    switch (rarity) {
      case 'rare': return 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]';
      case 'legendary': return 'border-yellow-400 shadow-[0_0_20px_rgba(250,196,21,0.5)]';
      case 'cursed': return 'border-red-600 shadow-[0_0_20_px_rgba(220,38,38,0.6)]';
      case 'god': return 'animate-rainbow-border border-white shadow-[0_0_30px_rgba(255,255,255,0.7)]';
      default: return 'border-zinc-800';
    }
  };

  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-8">
      {/* CUSTOM BULK CONFIRMATION MODAL */}
      {pendingBulkAction !== null && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/90 backdrop-blur-2xl animate-in fade-in duration-300">
          <div className="w-full max-w-lg bg-zinc-900 border-4 border-blue-500 rounded-[3.5rem] p-10 shadow-[0_0_100px_rgba(59,130,246,0.2)] space-y-8 animate-in zoom-in-95">
            <div className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 bg-blue-500/20 rounded-3xl flex items-center justify-center text-blue-500 border-2 border-blue-500/40 mb-2">
                <ShieldAlert size={48}/>
              </div>
              <h3 className="text-4xl font-bungee text-white uppercase tracking-tight leading-none">
                {pendingBulkAction ? 'SHOWCASE ALL?' : 'HIDE EVERYTHING?'}
              </h3>
              <p className="text-xs font-black text-zinc-500 uppercase tracking-widest leading-relaxed">
                {pendingBulkAction 
                  ? "This will make your entire vault visible to the world in the Global Top Rot feed." 
                  : "This will encrypt your entire vault from the public. Only you will see your rots."
                }
              </p>
            </div>
            
            <div className="flex flex-col gap-4">
              <button 
                onClick={() => executeBulkVisibility(pendingBulkAction)}
                className={`w-full py-6 rounded-2xl font-bungee text-2xl uppercase shadow-lg transition-all active:translate-y-2 active:shadow-none flex items-center justify-center gap-3 ${pendingBulkAction ? 'bg-emerald-500 text-black shadow-[0_8px_0_0_#065f46]' : 'bg-red-600 text-white shadow-[0_8px_0_0_#991b1b]'}`}
              >
                <Check size={28}/> Confirm Shift
              </button>
              <button 
                onClick={() => setPendingBulkAction(null)}
                className="w-full py-5 bg-zinc-800 text-zinc-400 border-2 border-zinc-700 rounded-2xl font-bungee text-sm uppercase hover:text-white transition-all flex items-center justify-center gap-2"
              >
                <X size={18}/> Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM DELETE CONFIRMATION MODAL */}
      {pendingDeleteMeme !== null && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/95 backdrop-blur-2xl animate-in fade-in duration-300">
          <div className="w-full max-w-lg bg-zinc-950 border-4 border-red-600 rounded-[3.5rem] p-10 shadow-[0_0_100px_rgba(220,38,38,0.2)] space-y-8 animate-in zoom-in-95">
            <div className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 bg-red-600/20 rounded-3xl flex items-center justify-center text-red-600 border-2 border-red-600/40 mb-2">
                <Skull size={48}/>
              </div>
              <h3 className="text-4xl font-bungee text-white uppercase tracking-tight leading-none">
                BANISH THIS ROT?
              </h3>
              <div className="space-y-2">
                <p className="text-xs font-black text-red-400 uppercase tracking-widest leading-relaxed">
                  THIS SIGMA IS ABOUT TO BE SENT TO OHIO FOREVER. 💀
                </p>
                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-tighter leading-relaxed">
                  THIS ROT WILL BE ERASED FROM REALITY. NO CLOUT, NO AURA, NO BACKSIES. THE VOID IS CALLING.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col gap-4">
              <button 
                onClick={executeDeleteMeme}
                className="w-full py-6 bg-red-600 text-white rounded-2xl font-bungee text-2xl uppercase shadow-[0_8px_0_0_#991b1b] transition-all active:translate-y-2 active:shadow-none flex items-center justify-center gap-3"
              >
                <Trash2 size={28}/> PURGE FOREVER
              </button>
              <button 
                onClick={() => setPendingDeleteMeme(null)}
                className="w-full py-5 bg-zinc-800 text-zinc-400 border-2 border-zinc-700 rounded-2xl font-bungee text-sm uppercase hover:text-white transition-all flex items-center justify-center gap-2"
              >
                <X size={18}/> SAVE THIS SIGMA
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <button onClick={() => setActiveView('editor')} className="p-3 bg-zinc-900 rounded-2xl shadow-lg hover:scale-110 active:scale-90 transition-transform">
              <ChevronLeft size={24}/>
            </button>
            <h2 className="text-5xl font-bungee text-blue-500 uppercase tracking-tighter">THE VAULT</h2>
            <button 
              onClick={handleManualRefresh} 
              className={`p-3 bg-zinc-900 rounded-2xl text-zinc-500 hover:text-white transition-all ${isRefreshing ? 'animate-spin' : ''}`}
            >
              <RefreshCw size={18}/>
            </button>
          </div>
          
          <div className="flex flex-col xl:flex-row gap-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-1"><ShieldAlert size={12} className="text-zinc-600" /><label className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Master Frequency Lock</label></div>
              <div className="flex items-center gap-2 p-1 bg-zinc-950 rounded-2xl border-2 border-zinc-800 shadow-xl w-fit">
                <button onClick={() => handleUpdateGlobalVisibility('private')} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase flex items-center gap-2 ${vaultVisibility === 'private' ? 'bg-blue-600 text-white shadow-md' : 'text-zinc-500 hover:text-zinc-300'}`} aria-label="Set Vault to Private"><Lock size={12}/> Private</button>
                <button onClick={() => handleUpdateGlobalVisibility('public')} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase flex items-center gap-2 ${vaultVisibility === 'public' ? 'bg-emerald-600 text-white shadow-md' : 'text-zinc-500 hover:text-zinc-300'}`} aria-label="Set Vault to Public"><Globe size={12}/> Public</button>
                <button onClick={() => handleUpdateGlobalVisibility('followers')} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase flex items-center gap-2 ${vaultVisibility === 'followers' ? 'bg-purple-600 text-white shadow-md' : 'text-zinc-500 hover:text-zinc-300'}`} aria-label="Set Vault to Followers Only"><Users size={12}/> Followers</button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 px-1"><ArrowUpDown size={12} className="text-zinc-600" /><label className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Sort & Filter</label></div>
              <div className="flex items-center gap-2 p-1 bg-zinc-950 rounded-2xl border-2 border-zinc-800 shadow-xl w-fit">
                <button onClick={() => setSortBy('date')} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${sortBy === 'date' ? 'bg-indigo-600 text-white shadow-md' : 'text-zinc-500 hover:text-zinc-300'}`} aria-label="Sort by Newest">Newest</button>
                <button onClick={() => setSortBy('rarity')} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${sortBy === 'rarity' ? 'bg-yellow-500 text-black shadow-md' : 'text-zinc-500 hover:text-zinc-300'}`} aria-label="Sort by Rarity">Rarity</button>
                <button onClick={() => setSortBy('favorite')} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${sortBy === 'favorite' ? 'bg-pink-600 text-white shadow-md' : 'text-zinc-500 hover:text-zinc-300'}`} aria-label="Sort by Favorites">Favs</button>
                <div className="w-px h-6 bg-zinc-800 mx-1"></div>
                <button onClick={() => setFavoritesOnly(!favoritesOnly)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase flex items-center gap-2 transition-all ${favoritesOnly ? 'bg-white text-black shadow-md' : 'text-zinc-500 hover:text-zinc-300'}`} aria-label="Filter Favorites Only"><Filter size={12}/> Favorites Only</button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 px-1"><ShieldAlert size={12} className="text-zinc-600" /><label className="text-[10px] font-black text-zinc-600 uppercase tracking-widest">Batch Actions</label></div>
              <div className="flex items-center gap-2 p-1 bg-zinc-950 rounded-2xl border-2 border-zinc-800 shadow-xl w-fit">
                <button onClick={() => setPendingBulkAction(true)} className="px-4 py-2 rounded-xl text-[10px] font-black uppercase flex items-center gap-2 text-emerald-500 hover:bg-emerald-500/10 transition-all"><EyeIcon size={12}/> Showcase All</button>
                <button onClick={() => setPendingBulkAction(false)} className="px-4 py-2 rounded-xl text-[10px] font-black uppercase flex items-center gap-2 text-red-500 hover:bg-red-500/10 transition-all"><EyeOff size={12}/> Hide All</button>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full md:w-64 space-y-2">
          <div className="flex justify-between text-[10px] font-black uppercase text-zinc-500 px-1">
            <span>STORAGE CAPACITY</span>
            <span>{allHistory.length} / {entitlements?.vaultLimit === 'unlimited' ? '∞' : (entitlements?.vaultLimit || 0)}</span>
          </div>
          <div className="h-4 bg-zinc-950 rounded-full border-2 border-zinc-800 overflow-hidden relative shadow-inner">
            <div className={`h-full transition-all duration-1000 ${vaultStoragePercent > 90 ? 'bg-red-500 shadow-[0_0_10px_#ef4444]' : vaultStoragePercent > 70 ? 'bg-yellow-400' : 'bg-blue-500'}`} style={{ width: `${entitlements?.vaultLimit === 'unlimited' ? 100 : Math.min(100, vaultStoragePercent)}%` }} />
          </div>
        </div>
      </div>
      
      <div className="relative min-h-[400px]">
        {isProcessing === 'bulk' && (
          <div className="fixed inset-0 z-[1000] flex flex-col items-center justify-center gap-6 bg-black/80 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-500 blur-[80px] opacity-40 animate-pulse"></div>
              <Loader2 className="w-24 h-24 text-blue-500 animate-spin relative" />
            </div>
            <div className="text-center space-y-3">
              <p className="font-bungee text-4xl text-white uppercase tracking-widest drop-shadow-lg">SYNCING FREQUENCIES</p>
              <p className="text-xs font-black text-blue-400 uppercase tracking-[0.4em] animate-pulse">Mass Update in Progress...</p>
            </div>
          </div>
        )}

        <div className={`grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 transition-all duration-700 ${isProcessing === 'bulk' ? 'scale-[0.95] blur-md opacity-20' : ''}`}>
          {filteredAndSortedHistory.length === 0 ? (
            <div className="col-span-full py-40 text-center font-bungee opacity-20 text-2xl uppercase border-4 border-dashed border-zinc-800 rounded-[4rem] bg-zinc-900/20 shadow-inner flex flex-col items-center justify-center gap-6">
              <Lock size={64} />
              <div className="space-y-2">
                <p>{favoritesOnly ? 'No Favorites Found' : 'Vault Empty'}</p>
                <p className="text-xs font-black">{favoritesOnly ? 'Heart some rots to see them here!' : 'Cook some rots to unlock batch actions!'}</p>
              </div>
            </div>
          ) : (
            filteredAndSortedHistory.map(meme => meme && (
              <div key={meme.id} className={`group relative aspect-square bg-zinc-900 rounded-[2rem] overflow-hidden border-4 shadow-xl transition-all hover:scale-105 active:scale-95 ${getRarityStyles(meme.rarity)}`}>
                <img src={meme.url} className="w-full h-full object-cover cursor-pointer" onClick={() => setPreviewImage(meme)} alt={meme.name || "Meme"} />
                
                {/* Favorites Toggle - Heart Icon always visible if favorite, or on hover */}
                <button 
                  onClick={(e) => { e.stopPropagation(); handleToggleFavorite(meme); }}
                  className={`absolute bottom-3 right-3 p-1.5 rounded-xl transition-all backdrop-blur-md shadow-lg active:scale-90 z-20 ${meme.is_favorite ? 'bg-pink-600 text-white scale-110 shadow-[0_0_15px_#db2777]' : 'bg-black/60 text-white/40 opacity-0 group-hover:opacity-100 hover:text-pink-400'}`}
                >
                  <Heart size={14} fill={meme.is_favorite ? "currentColor" : "none"} />
                </button>

                <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                  {isProcessing === meme.id ? <div className="p-1.5 bg-black/60 rounded-xl text-white"><Loader2 size={14} className="animate-spin" /></div> : (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteClick(meme); }} className="p-1.5 bg-red-600/80 rounded-xl text-white backdrop-blur-md hover:bg-red-500 transition-all shadow-lg active:scale-90"><Trash2 size={14}/></button>
                      <button onClick={(e) => { e.stopPropagation(); handleToggleMemePublic(meme); }} className={`p-1.5 rounded-xl transition-all backdrop-blur-md shadow-lg active:scale-90 ${meme.is_public ? 'bg-emerald-400 text-black border border-white/20' : 'bg-zinc-800/80 text-white hover:text-emerald-400'}`}>
                        {meme.is_public ? <Eye size={14}/> : <EyeOff size={14}/>}
                      </button>
                    </>
                  )}
                </div>
                {meme.is_public && <div className="absolute bottom-3 left-3 px-2 py-0.5 bg-emerald-500 rounded font-bungee text-[6px] text-black shadow-lg border border-black/10 flex items-center gap-1"><Sparkles size={6} /> SHOWCASE</div>}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
